<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '../services/api'
import ConfirmDialog from '../components/ConfirmDialog.vue'

const route = useRoute()
const router = useRouter()

const data = ref(null)
const error = ref('')
const toast = ref('')
const publishingId = ref(null)
const confirmSubject = ref(null)
const statusFilter = ref('all')
const search = ref('')
const statLabels = { students: 'Học sinh', parents: 'Phụ huynh', subjects: 'Môn đang mở', lessons: 'Bài học', questions: 'Câu hỏi', attempts: 'Lượt làm bài' }
const shortcuts = [
  { to: '/quan-tri/noi-dung', icon: '＋', title: 'Soạn bài học', text: 'Chọn lớp → môn → chương, rồi thêm bài, câu hỏi hoặc đề.' },
  { to: '/quan-tri/tai-khoan?tao=student', icon: '☺', title: 'Thêm học sinh', text: 'Tạo tài khoản, gắn phụ huynh và theo dõi tiến độ từng em.' },
  { to: '/quan-tri/nhap-xuat', icon: '⇅', title: 'Nhập / xuất học liệu', text: 'Đưa gói JSON của một môn lên, xem trước thay đổi rồi áp dụng.' },
]

const grades = computed(() => [...new Set((data.value?.subjects || []).map(subject => subject.grade))].sort((a, b) => a - b))
const activeGrade = computed(() => {
  const wanted = Number(route.query.lop)
  return grades.value.includes(wanted) ? wanted : grades.value[0]
})
const gradeSubjects = computed(() => (data.value?.subjects || []).filter(subject => subject.grade === activeGrade.value))
const statusCounts = computed(() => ({
  all: gradeSubjects.value.length,
  published: gradeSubjects.value.filter(subject => subject.is_published).length,
  draft: gradeSubjects.value.filter(subject => !subject.is_published).length,
}))
const visibleSubjects = computed(() => {
  const term = search.value.trim().toLowerCase()
  return gradeSubjects.value.filter(subject =>
    (statusFilter.value === 'all' || (statusFilter.value === 'published') === subject.is_published)
    && (!term || subject.title.toLowerCase().includes(term)))
})
const statusOptions = [['all', 'Tất cả'], ['published', 'Đang mở'], ['draft', 'Nháp']]

function selectGrade(grade) {
  statusFilter.value = 'all'
  search.value = ''
  router.replace({ query: { ...route.query, lop: grade } })
}

function requestToggle(subject) {
  if (subject.is_published) confirmSubject.value = subject
  else togglePublication(subject)
}

function counts(subject) {
  const chapters = subject.semesters.flatMap(sem => sem.chapters)
  const lessons = chapters.flatMap(chapter => chapter.lessons)
  return { chapters: chapters.length, lessons: lessons.length, drafts: lessons.filter(lesson => !lesson.is_published).length }
}

async function load() {
  error.value = ''
  try { data.value = await api('/admin/overview') }
  catch (err) { error.value = err.message }
}

async function togglePublication(subject) {
  if (publishingId.value !== null) return
  publishingId.value = subject.id
  error.value = ''
  try {
    const published = !subject.is_published
    await api('/admin/subjects/' + subject.id, { method: 'PUT', body: JSON.stringify({ is_published: published, enroll_students: published }) })
    toast.value = published ? 'Đã xuất bản môn và đăng ký cho học sinh cùng lớp' : 'Đã đưa môn về nháp; lịch sử học vẫn được giữ'
    setTimeout(() => toast.value = '', 3000)
    confirmSubject.value = null
    await load()
  } catch (err) { error.value = err.message }
  finally { publishingId.value = null }
}

onMounted(load)
</script>

<template>
  <header><p class="eyebrow">Trung tâm quản trị</p><h1 class="page-title">Tổng quan</h1><p class="page-subtitle">Theo dõi nhanh số liệu, mở việc thường làm và bật / tắt xuất bản từng môn.</p></header>
  <div v-if="error" class="error-banner" style="margin-top:20px">{{ error }} <button class="btn btn-light" @click="load">Thử lại</button></div>
  <div v-if="!data && !error" class="loading-block"><div><div class="loader"></div><p>Đang tải dữ liệu quản trị…</p></div></div>
  <template v-else-if="data">
    <section class="admin-stats">
      <div v-for="(label, key) in statLabels" :key="key" class="card admin-stat"><strong>{{ data.stats[key] }}</strong><small>{{ label }}</small></div>
    </section>

    <section class="admin-shortcuts" aria-label="Việc thường làm">
      <RouterLink v-for="item in shortcuts" :key="item.to" :to="item.to" class="card admin-shortcut"><span>{{ item.icon }}</span><div><strong>{{ item.title }}</strong><small>{{ item.text }}</small></div><b>→</b></RouterLink>
    </section>

    <div class="section-heading"><div><h2>Các môn theo lớp</h2><p>Học sinh chỉ thấy các môn đang mở của đúng lớp mình. Môn nháp chỉ quản trị viên nhìn thấy.</p></div></div>
    <div v-if="!data.subjects.length" class="card empty-state"><strong>Chưa có môn học</strong>Vào Soạn nội dung để tạo môn đầu tiên.</div>
    <template v-else>
      <div class="grade-bar" role="tablist" aria-label="Chọn lớp">
        <button v-for="grade in grades" :key="grade" type="button" role="tab" :aria-selected="grade === activeGrade" :class="{ active: grade === activeGrade }" @click="selectGrade(grade)">Lớp {{ grade }}<small>{{ data.subjects.filter(subject => subject.grade === grade).length }} môn</small></button>
        <RouterLink :to="`/quan-tri/noi-dung?lop=${activeGrade}`" class="text-link grade-bar-link">Soạn nội dung lớp {{ activeGrade }} →</RouterLink>
      </div>

      <div class="subject-toolbar">
        <div class="status-filter" role="radiogroup" aria-label="Lọc theo trạng thái">
          <button v-for="[key, label] in statusOptions" :key="key" type="button" role="radio" :aria-checked="statusFilter === key" :class="{ active: statusFilter === key }" @click="statusFilter = key">{{ label }} <small>{{ statusCounts[key] }}</small></button>
        </div>
        <input v-if="gradeSubjects.length > 4" v-model="search" type="search" class="subject-search" :placeholder="`Tìm môn lớp ${activeGrade}…`" aria-label="Tìm môn">
      </div>

      <div v-if="!visibleSubjects.length" class="card empty-state"><strong>Không có môn phù hợp</strong>Thử bỏ bộ lọc hoặc từ khóa tìm kiếm.</div>
      <section v-else class="admin-subject-grid" role="tabpanel" :aria-label="`Môn lớp ${activeGrade}`">
        <article v-for="subject in visibleSubjects" :key="subject.id" class="card admin-subject-card" :class="{ draft: !subject.is_published }">
          <RouterLink :to="`/quan-tri/noi-dung?lop=${subject.grade}&chon=subject-${subject.id}`" class="admin-subject-card-main">
            <div class="subject-icon" :class="subject.color">{{ subject.icon }}</div>
            <div>
              <strong>{{ subject.title }}</strong>
              <span class="chip" :class="subject.is_published ? '' : 'purple'">{{ subject.is_published ? 'Đang mở' : 'Nháp' }}</span>
            </div>
          </RouterLink>
          <dl class="admin-subject-stats">
            <div><dt>Chương</dt><dd>{{ counts(subject).chapters }}</dd></div>
            <div><dt>Bài</dt><dd>{{ counts(subject).lessons }}</dd></div>
            <div><dt>Bài nháp</dt><dd>{{ counts(subject).drafts }}</dd></div>
          </dl>
          <div class="admin-subject-card-actions">
            <RouterLink :to="`/quan-tri/noi-dung?lop=${subject.grade}&chon=subject-${subject.id}`" class="btn btn-light">Soạn nội dung</RouterLink>
            <button type="button" class="publish-toggle" :class="{ primary: !subject.is_published }" :disabled="publishingId !== null" @click="requestToggle(subject)">{{ publishingId === subject.id ? 'Đang lưu…' : subject.is_published ? 'Đưa về nháp' : 'Xuất bản' }}</button>
          </div>
        </article>
      </section>
    </template>

    <ConfirmDialog :open="!!confirmSubject" icon="!" :title="`Đưa ${confirmSubject?.title || 'môn'} về nháp?`" confirm-label="Đưa về nháp" :busy="publishingId !== null" busy-label="Đang lưu…" @cancel="confirmSubject = null" @confirm="togglePublication(confirmSubject)">
      Học sinh lớp {{ confirmSubject?.grade }} sẽ không còn thấy môn này cho tới khi bạn xuất bản lại. Lịch sử học của các em vẫn được giữ.
    </ConfirmDialog>
  </template>
  <div v-if="toast" class="toast">✓ {{ toast }}</div>
</template>

<style scoped>
.admin-shortcuts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-top: 18px; }
.admin-shortcut { display: flex; align-items: center; gap: 14px; padding: 18px; transition: border-color .2s, transform .2s; }
.admin-shortcut:hover { border-color: #c9c3f5; transform: translateY(-1px); }
.admin-shortcut > span { flex: 0 0 42px; height: 42px; display: grid; place-items: center; border-radius: 12px; background: var(--mint); color: var(--teal); font-size: 20px; font-weight: 800; }
.admin-shortcut strong, .admin-shortcut small { display: block; }
.admin-shortcut strong { font-size: 14px; }
.admin-shortcut small { margin-top: 4px; color: var(--muted); font-size: 11px; line-height: 1.5; }
.admin-shortcut b { margin-left: auto; color: var(--teal); }
.grade-bar-link { margin-left: auto; padding: 0 6px; }
.subject-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin: 14px 0; }
.status-filter { display: inline-flex; gap: 4px; padding: 4px; border-radius: 12px; background: #f1f0f8; }
.status-filter button { padding: 7px 12px; border: 0; border-radius: 9px; background: transparent; color: var(--muted); font-size: 12px; font-weight: 700; cursor: pointer; }
.status-filter button small { margin-left: 2px; font-weight: 600; opacity: .8; }
.status-filter button.active { background: #fff; color: var(--ink); box-shadow: 0 1px 3px rgba(30, 30, 70, .12); }
.subject-search { width: min(260px, 100%); height: 38px; padding: 0 12px; border: 1px solid var(--border); border-radius: 11px; background: #fff; font-size: 13px; }
.admin-subject-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px; }
.admin-subject-card { display: flex; flex-direction: column; gap: 14px; padding: 18px; transition: border-color .2s, transform .2s; }
.admin-subject-card:hover { border-color: #c9c3f5; transform: translateY(-1px); }
.admin-subject-card.draft { border-style: dashed; border-color: #cfc9f3; background: #fcfbff; }
.admin-subject-card-main { display: flex; align-items: center; gap: 12px; min-width: 0; }
.admin-subject-card-main .subject-icon { flex: 0 0 46px; width: 46px; height: 46px; font-size: 17px; }
.admin-subject-card-main strong { display: block; margin-bottom: 6px; font-size: 15px; }
.admin-subject-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin: 0; padding: 10px 0; border-block: 1px solid var(--border); }
.admin-subject-stats div { text-align: center; }
.admin-subject-stats dt { color: var(--muted); font-size: 10px; font-weight: 700; }
.admin-subject-stats dd { margin: 2px 0 0; font-size: 16px; font-weight: 800; }
.admin-subject-card-actions { display: flex; align-items: center; gap: 8px; margin-top: auto; }
.admin-subject-card-actions .btn { flex: 1; min-height: 38px; font-size: 12px; padding: 0 12px; }
.publish-toggle { padding: 8px 6px; border: 0; background: none; color: var(--muted); font-size: 12px; font-weight: 700; cursor: pointer; }
.publish-toggle:hover:not(:disabled) { color: #9d3535; text-decoration: underline; }
.publish-toggle.primary { color: var(--teal); }
.publish-toggle.primary:hover:not(:disabled) { color: var(--teal); }
.publish-toggle:disabled { opacity: .5; cursor: default; }
@media (max-width: 900px) { .admin-shortcuts { grid-template-columns: 1fr; } }
@media (max-width: 640px) {
  .grade-bar { flex-wrap: nowrap; overflow-x: auto; }
  .grade-bar button { flex-shrink: 0; }
  .grade-bar-link { display: none; }
  .subject-search { width: 100%; }
}
</style>
